import type {
    ComposerAttributeNode,
    ComposerAttributeOptionNode,
    ComposerAreaNode,
    ComposerAttributeType,
    ComposerEcommercePlatform,
    ComposerMerchantProductOption,
    ComposerOptionActionNode,
    ComposerOptionActionsType
} from '../../../configurator-components/composer-editors/attributes/composerAttributesModels';

const ATTRIBUTE_NODE_TYPE = 'Attribute' as ComposerAttributeType;
const ATTRIBUTE_GROUP_NODE_TYPE = 'AttributeGroup' as ComposerAttributeType;
const ATTRIBUTE_STEP_NODE_TYPE = 'AttributeStep' as ComposerAttributeType;
const BIGCOMMERCE_PLATFORM = 9 as ComposerEcommercePlatform;
const CUSTOMIZABLE_AREA_ATTRIBUTE_TYPE = 5 as MPlaza.AttributeType;
const ACTION_TYPE = {
    SHOW_OBJECTS: 99,
    HIDE_OBJECTS: 2,
    SET_OBJECT_MATERIAL: 3,
    SET_SUB_OBJECT_MATERIAL: 7,
    SET_MATERIAL_COLOR: 4,
    MOVE_CAMERA: 5,
    SET_DESIGN_MATERIAL: 11,
    SET_CAMERA_PIVOT: 8,
    RESET_CAMERA_PIVOT: 9,
    SET_MATERIAL_PARAMETER: 17,
    SET_PRINTING_METHOD: 18
} as const;

const ACTION_TYPE_LABELS: Record<number, string> = {
    [ACTION_TYPE.SHOW_OBJECTS]: 'Show objects',
    [ACTION_TYPE.HIDE_OBJECTS]: 'Hide objects',
    [ACTION_TYPE.SET_OBJECT_MATERIAL]: 'Set object material',
    [ACTION_TYPE.SET_SUB_OBJECT_MATERIAL]: 'Set sub-object material',
    [ACTION_TYPE.SET_MATERIAL_COLOR]: 'Set material color',
    [ACTION_TYPE.MOVE_CAMERA]: 'Move camera',
    [ACTION_TYPE.SET_DESIGN_MATERIAL]: 'Set design material',
    [ACTION_TYPE.SET_CAMERA_PIVOT]: 'Set camera pivot',
    [ACTION_TYPE.RESET_CAMERA_PIVOT]: 'Reset camera pivot',
    [ACTION_TYPE.SET_MATERIAL_PARAMETER]: 'Set material parameter',
    [ACTION_TYPE.SET_PRINTING_METHOD]: 'Set printing method'
};

export const ComposerValidationErrorCode = {
    ATTRIBUTE_NAME_REQUIRED: 'attribute-name-required',
    ATTRIBUTE_NAME_NOT_UNIQUE: 'attribute-name-not-unique',
    ATTRIBUTE_CODE_REQUIRED: 'attribute-code-required',
    ATTRIBUTE_CODE_NOT_UNIQUE: 'attribute-code-not-unique',
    ATTRIBUTE_WITHOUT_OPTIONS: 'attribute-without-options',
    ATTRIBUTE_WITHOUT_ENABLED_OPTIONS: 'attribute-without-enabled-options',
    ATTRIBUTE_WITHOUT_DEFAULT_OPTION: 'attribute-without-default-option',
    ATTRIBUTE_WITH_MULTIPLE_DEFAULT_OPTIONS: 'attribute-with-multiple-default-options',
    ATTRIBUTE_PARENT_NOT_FOUND: 'attribute-parent-not-found',
    GROUP_NAME_REQUIRED: 'group-name-required',
    STEP_NAME_REQUIRED: 'step-name-required',
    STEP_PARENT_NOT_FOUND: 'step-parent-not-found',
    PRESET_NAME_REQUIRED: 'preset-name-required',
    PRESET_SOURCE_REQUIRED: 'preset-source-required',
    PRESET_SOURCE_MIXED: 'preset-source-mixed',
    PRESET_PARENT_NOT_FOUND: 'preset-parent-not-found',
    BIGCOMMERCE_ATTRIBUTE_NOT_CONNECTED: 'bigcommerce-attribute-not-connected',
    STORE_ATTRIBUTE_NOT_FOUND: 'store-attribute-not-found',
    STORE_ATTRIBUTE_NOT_UNIQUE: 'store-attribute-not-unique',
    STORE_CODE_INVALID: 'store-code-invalid',
    OPTION_NAME_REQUIRED: 'option-name-required',
    OPTION_NAME_NOT_UNIQUE: 'option-name-not-unique',
    OPTION_CODE_REQUIRED: 'option-code-required',
    OPTION_CODE_NOT_UNIQUE: 'option-code-not-unique',
    OPTION_NOT_FROM_PLATFORM: 'option-not-from-platform',
    OPTION_FROM_PLATFORM_WITH_LOCAL_ATTRIBUTE: 'option-from-platform-with-local-attribute',
    STORE_OPTION_NOT_FOUND: 'store-option-not-found',
    STORE_OPTION_NOT_UNIQUE: 'store-option-not-unique',
    DEFAULT_OPTION_DISABLED: 'default-option-disabled',
    PRICING_WITHOUT_OPTION: 'pricing-without-option',
    MULTIPLE_UNCONDITIONAL_PRICINGS: 'multiple-unconditional-pricings',
    LINK_INCOMPLETE: 'link-incomplete',
    LINK_ATTRIBUTE_NOT_FOUND: 'link-attribute-not-found',
    LINK_OPTION_NOT_FOUND: 'link-option-not-found',
    LINK_SELF_REFERENCE: 'link-self-reference',
    LINK_OPERATION_INVALID: 'link-operation-invalid',
    ACTION_SELECTOR_REQUIRED: 'action-selector-required',
    ACTION_VALUE_REQUIRED: 'action-value-required',
    ACTION_PROPERTY_REQUIRED: 'action-property-required',
    ACTION_SELECTOR_TOO_LONG: 'action-selector-too-long',
    ACTION_TYPE_UNSUPPORTED: 'action-type-unsupported',
    AREA_OBJECTS_REQUIRED: 'area-objects-required',
    AREA_OBJECTS_NOT_MESH: 'area-objects-not-mesh'
} as const;

type ValidatableNode = Pick<ComposerAttributeNode | ComposerAttributeOptionNode | ComposerOptionActionNode, 'brokenValues'>;

export interface ComposerValidationContext {
    ecommercePlatform?: ComposerEcommercePlatform | null;
    merchantProductOptions?: ComposerMerchantProductOption[];
    merchantProductOptionsError?: string | null;
}

const normalizeComparableValue = (value: string | undefined | null) => (value ?? '').trim().toLocaleLowerCase();
const isConnectedToStore = (node: { platformAttribute?: { id: string } }) => Boolean(node.platformAttribute?.id?.trim());
const formatNodeName = (name: string | undefined | null, fallback: string) => name?.trim() || fallback;
const formatActionType = (type: ComposerOptionActionsType) => ACTION_TYPE_LABELS[type] || 'Action';

const countValues = <T>(items: T[], getValue: (item: T) => string) => {
    const counts = new Map<string, number>();
    items.forEach((item) => {
        const value = normalizeComparableValue(getValue(item));
        if (value) counts.set(value, (counts.get(value) ?? 0) + 1);
    });
    return counts;
};

const addValidationError = (node: ValidatableNode, code: string, message: string) => {
    node.brokenValues.set(code, message);
};

export const hasComposerValidationError = (node: ValidatableNode | null | undefined, ...codes: string[]) => {
    if (!node) return false;
    return Array.from(node.brokenValues.keys()).some((key) => codes.some((code) => key === code || key.startsWith(`${code}:`)));
};

/** Empty/transform nodes can be selected in a scene, but cannot receive a printable texture. */
export const isPrintableComposerMesh = (mesh: BABYLON.AbstractMesh) =>
    typeof mesh.getTotalVertices === 'function' && mesh.getTotalVertices() > 0 && !!mesh.subMeshes?.length;

export const validateComposerAreas = (areaList: ComposerAreaNode[], sceneObjects: BABYLON.AbstractMesh[] = []): ComposerAreaNode[] => {
    // Sharing a mesh across areas is supported and is not a validation problem.
    return areaList.map((area) => {
        const validatedArea = { ...area, brokenValues: new Map<string, string>() };
        const objectIDs = [
            ...new Set(
                (validatedArea.objects || '')
                    .split(';')
                    .map((id) => id.trim())
                    .filter(Boolean)
            )
        ];
        if (!objectIDs.length) {
            addValidationError(
                validatedArea,
                ComposerValidationErrorCode.AREA_OBJECTS_REQUIRED,
                `The customizable area “${formatNodeName(validatedArea.name, 'Unnamed area')}” needs at least one object/mesh.`
            );
        } else if (sceneObjects.length > 0) {
            const validMeshIDs = new Set(sceneObjects.filter(isPrintableComposerMesh).map((mesh) => mesh.id));
            if (objectIDs.some((id) => !validMeshIDs.has(id))) {
                addValidationError(
                    validatedArea,
                    ComposerValidationErrorCode.AREA_OBJECTS_NOT_MESH,
                    `The customizable area “${formatNodeName(validatedArea.name, 'Unnamed area')}” includes an Empty or an unavailable object. Select a mesh with geometry in Placement → Objects.`
                );
            }
        }
        return validatedArea;
    });
};

const readStoreCodeID = (code: string | undefined | null) => {
    if (!code?.trim().startsWith('{')) return '';
    try {
        const parsed = JSON.parse(code);
        return typeof parsed?.id === 'string' ? parsed.id.trim() : '';
    } catch {
        return '';
    }
};

const validateStoreCode = (node: ComposerAttributeNode | ComposerAttributeOptionNode, label: string) => {
    const connected = isConnectedToStore(node);
    const serializedID = readStoreCodeID(node.code);
    // Product/variant payloads used by Shopify and BigCommerce charges are JSON too,
    // but only the { id, label } shape represents an attribute/option store mapping.
    const serializedAsStore = Boolean(serializedID);
    const selectedID = node.platformAttribute?.id?.trim() || '';

    if (connected && (!serializedAsStore || !serializedID || serializedID !== selectedID)) {
        addValidationError(
            node,
            ComposerValidationErrorCode.STORE_CODE_INVALID,
            `${label} is connected to the store, but its saved store code does not match the selected store item.`
        );
    } else if (!connected && serializedAsStore) {
        addValidationError(node, ComposerValidationErrorCode.STORE_CODE_INVALID, `${label} contains a store code but is not connected to a valid store item.`);
    }
};

const validateLinks = (node: ComposerAttributeNode | ComposerAttributeOptionNode, ownerAttributeID: string, attributes: ComposerAttributeNode[]) => {
    node.links.forEach((link, index) => {
        const issueKey = `:${index}`;
        if (!link.attributeID || !link.optionID) {
            addValidationError(
                node,
                `${ComposerValidationErrorCode.LINK_INCOMPLETE}${issueKey}`,
                `Condition ${index + 1} is incomplete. Select both an attribute and an option.`
            );
            return;
        }

        if (!['Equal', 'NotEqual', 'In', 'NotIn'].includes(link.operation)) {
            addValidationError(
                node,
                `${ComposerValidationErrorCode.LINK_OPERATION_INVALID}${issueKey}`,
                `Condition ${index + 1} uses an unsupported comparison. Select the comparison again.`
            );
        }

        if (link.attributeID === ownerAttributeID) {
            addValidationError(
                node,
                `${ComposerValidationErrorCode.LINK_SELF_REFERENCE}${issueKey}`,
                `Condition ${index + 1} refers to the same attribute and can never safely control itself.`
            );
            return;
        }

        const targetAttribute = attributes.find((attribute) => attribute.type === ATTRIBUTE_NODE_TYPE && attribute.id === link.attributeID);
        if (!targetAttribute) {
            addValidationError(
                node,
                `${ComposerValidationErrorCode.LINK_ATTRIBUTE_NOT_FOUND}${issueKey}`,
                `Condition ${index + 1} refers to an attribute that no longer exists.`
            );
            return;
        }

        if (!targetAttribute.options.some((option) => option.id === link.optionID)) {
            addValidationError(
                node,
                `${ComposerValidationErrorCode.LINK_OPTION_NOT_FOUND}${issueKey}`,
                `Condition ${index + 1} refers to an option that no longer exists in “${formatNodeName(targetAttribute.name, 'the target attribute')}”.`
            );
        }
    });
};

export const validateComposerActions = (actionList: ComposerOptionActionNode[]): ComposerOptionActionNode[] =>
    actionList.map((action) => {
        const validatedAction = { ...action, brokenValues: new Map<string, string>() };
        const actionLabel = formatActionType(validatedAction.type);
        const selector = validatedAction.selector?.trim() || '';
        const value = validatedAction.value?.trim() || '';
        const property = validatedAction.property?.trim() || '';

        if (selector.length > 950) {
            addValidationError(
                validatedAction,
                ComposerValidationErrorCode.ACTION_SELECTOR_TOO_LONG,
                `${actionLabel} targets too many objects or materials. Split it into more than one action.`
            );
        }

        const requireSelector = () => {
            if (!selector) {
                addValidationError(
                    validatedAction,
                    ComposerValidationErrorCode.ACTION_SELECTOR_REQUIRED,
                    `${actionLabel} needs at least one target object, material or filter.`
                );
            }
        };
        const requireValue = (description: string) => {
            if (!value) addValidationError(validatedAction, ComposerValidationErrorCode.ACTION_VALUE_REQUIRED, `${actionLabel} needs ${description}.`);
        };

        switch (validatedAction.type) {
            case ACTION_TYPE.SHOW_OBJECTS:
            case ACTION_TYPE.HIDE_OBJECTS:
                requireSelector();
                break;
            case ACTION_TYPE.SET_OBJECT_MATERIAL:
            case ACTION_TYPE.SET_SUB_OBJECT_MATERIAL:
            case ACTION_TYPE.SET_DESIGN_MATERIAL:
                requireSelector();
                requireValue('a material');
                break;
            case ACTION_TYPE.SET_MATERIAL_COLOR:
                requireSelector();
                if (!property) {
                    addValidationError(validatedAction, ComposerValidationErrorCode.ACTION_PROPERTY_REQUIRED, `${actionLabel} needs a color property.`);
                }
                requireValue('a color');
                break;
            case ACTION_TYPE.SET_MATERIAL_PARAMETER:
                requireSelector();
                if (!property) {
                    addValidationError(validatedAction, ComposerValidationErrorCode.ACTION_PROPERTY_REQUIRED, `${actionLabel} needs a material property.`);
                }
                break;
            case ACTION_TYPE.SET_PRINTING_METHOD:
                requireValue('a printing method');
                break;
            case ACTION_TYPE.MOVE_CAMERA:
                if (!selector && !value) {
                    addValidationError(validatedAction, ComposerValidationErrorCode.ACTION_VALUE_REQUIRED, `${actionLabel} needs a camera position.`);
                }
                break;
            case ACTION_TYPE.SET_CAMERA_PIVOT:
                requireValue('a camera pivot');
                break;
            case ACTION_TYPE.RESET_CAMERA_PIVOT:
                break;
            default:
                addValidationError(
                    validatedAction,
                    ComposerValidationErrorCode.ACTION_TYPE_UNSUPPORTED,
                    'This action type is no longer supported. Remove it or select a supported action.'
                );
        }

        return validatedAction;
    });

const getRawPricings = (attribute: ComposerAttributeNode): any[] => {
    const pricings = attribute.data?.pricings;
    return Array.isArray(pricings) ? pricings : [];
};

const isUnconditionalPricing = (attribute: ComposerAttributeNode, pricing: ComposerAttributeNode['pricings'][number]) => {
    const rawPricings = getRawPricings(attribute);
    const rawPricing =
        rawPricings.find(
            (candidate) =>
                pricing.attributePricingID > 0 && (candidate?.get?.('attributePricingID') ?? candidate?.attributePricingID) === pricing.attributePricingID
        ) ?? rawPricings[attribute.pricings.indexOf(pricing)];
    if (!rawPricing) return true;
    const expression = rawPricing.get?.('constraintExpression') ?? rawPricing.constraintExpression;
    const propositions = expression?.get?.('propositions') ?? expression?.propositions;
    return !expression || !Array.isArray(propositions) || propositions.length === 0;
};

export const validateComposerOptions = (
    attribute: ComposerAttributeNode,
    optionList: ComposerAttributeOptionNode[],
    allNodes: ComposerAttributeNode[] = [attribute],
    context: ComposerValidationContext = {}
): ComposerAttributeOptionNode[] => {
    const attributes = allNodes.filter((node) => node.type === ATTRIBUTE_NODE_TYPE);
    const options = optionList.map((option) => ({
        ...option,
        brokenValues: new Map<string, string>(),
        actions: validateComposerActions(option.actions)
    }));
    const nameCounts = countValues(options, (option) => option.name);
    const localOptions = options.filter((option) => !isConnectedToStore(option));
    const localCodeCounts = countValues(localOptions, (option) => option.code);
    const storeOptionCounts = countValues(options.filter(isConnectedToStore), (option) => option.platformAttribute?.id || '');
    const storeAttribute = context.merchantProductOptions?.find((candidate) => candidate.optionID === attribute.platformAttribute?.id);
    const canValidateStoreCatalog = Array.isArray(context.merchantProductOptions) && !context.merchantProductOptionsError;
    const unconditionalPricingCounts = countValues(
        (attribute.pricings ?? []).filter((pricing) => pricing.selectedOptionGuid && isUnconditionalPricing(attribute, pricing)),
        (pricing) => pricing.selectedOptionGuid
    );

    options.forEach((option) => {
        const optionName = formatNodeName(option.name, 'This option');
        const hasCustomPricing = (attribute.pricings ?? []).some((pricing) => pricing.selectedOptionGuid === option.id);
        if (!normalizeComparableValue(option.name)) {
            addValidationError(option, ComposerValidationErrorCode.OPTION_NAME_REQUIRED, 'This option needs a name.');
        }

        if ((nameCounts.get(normalizeComparableValue(option.name)) ?? 0) > 1) {
            addValidationError(
                option,
                ComposerValidationErrorCode.OPTION_NAME_NOT_UNIQUE,
                `Another option in “${formatNodeName(attribute.name, 'this attribute')}” uses the name “${option.name.trim()}”. Option names must be unique within an attribute.`
            );
        }

        if (!isConnectedToStore(option) && !normalizeComparableValue(option.code) && !hasCustomPricing) {
            addValidationError(option, ComposerValidationErrorCode.OPTION_CODE_REQUIRED, `The local option “${optionName}” needs a code.`);
        }

        if (!isConnectedToStore(option) && (localCodeCounts.get(normalizeComparableValue(option.code)) ?? 0) > 1) {
            addValidationError(
                option,
                ComposerValidationErrorCode.OPTION_CODE_NOT_UNIQUE,
                `Another local option in “${formatNodeName(attribute.name, 'this attribute')}” uses the code “${option.code.trim()}”. Codes must be unique for options not connected to the store.`
            );
        }

        if (isConnectedToStore(option) && (storeOptionCounts.get(normalizeComparableValue(option.platformAttribute?.id)) ?? 0) > 1) {
            addValidationError(
                option,
                ComposerValidationErrorCode.STORE_OPTION_NOT_UNIQUE,
                `More than one option is connected to the store option “${option.platformAttribute?.label || option.platformAttribute?.id}”.`
            );
        }

        if (isConnectedToStore(attribute) && !isConnectedToStore(option)) {
            addValidationError(
                option,
                ComposerValidationErrorCode.OPTION_NOT_FROM_PLATFORM,
                `The option “${optionName}” is not connected to a store option, but its attribute “${formatNodeName(attribute.name, 'Attribute')}” is connected to the store.`
            );
        } else if (!isConnectedToStore(attribute) && isConnectedToStore(option)) {
            addValidationError(
                option,
                ComposerValidationErrorCode.OPTION_FROM_PLATFORM_WITH_LOCAL_ATTRIBUTE,
                `The option “${optionName}” is connected to the store, but its attribute is local.`
            );
        }

        validateStoreCode(option, `The option “${optionName}”`);

        if (
            canValidateStoreCatalog &&
            isConnectedToStore(attribute) &&
            isConnectedToStore(option) &&
            storeAttribute &&
            !storeAttribute.values.some((candidate) => candidate.valueID === option.platformAttribute?.id)
        ) {
            addValidationError(
                option,
                ComposerValidationErrorCode.STORE_OPTION_NOT_FOUND,
                `The connected store option “${option.platformAttribute?.label || option.platformAttribute?.id}” is no longer available in the store attribute.`
            );
        }

        if (attribute.enabled !== false && option.isDefault && option.enabled === false) {
            addValidationError(option, ComposerValidationErrorCode.DEFAULT_OPTION_DISABLED, `The default option “${optionName}” is disabled.`);
        }

        if ((unconditionalPricingCounts.get(normalizeComparableValue(option.id)) ?? 0) > 1) {
            addValidationError(
                option,
                ComposerValidationErrorCode.MULTIPLE_UNCONDITIONAL_PRICINGS,
                `The option “${optionName}” has more than one price without conditions. Keep only one unconditional price.`
            );
        }

        validateLinks(option, attribute.id, attributes);
    });

    return options;
};

export const upsertAndValidateComposerOption = (
    attribute: ComposerAttributeNode,
    option: ComposerAttributeOptionNode,
    allNodes: ComposerAttributeNode[] = [attribute],
    context: ComposerValidationContext = {}
): ComposerAttributeNode => {
    const optionExists = attribute.options.some((candidate) => candidate.id === option.id);
    let nextOptions = optionExists ? attribute.options.map((candidate) => (candidate.id === option.id ? option : candidate)) : [...attribute.options, option];
    if (nextOptions.length === 1 && !nextOptions[0].isDefault) {
        nextOptions = [{ ...nextOptions[0], isDefault: true }];
    }

    return {
        ...attribute,
        options: validateComposerOptions(attribute, nextOptions, allNodes, context)
    };
};

export const validateComposerAttributes = (attributeList: ComposerAttributeNode[], context: ComposerValidationContext = {}): ComposerAttributeNode[] => {
    const resetNodes = attributeList.map((attribute) => ({ ...attribute, brokenValues: new Map<string, string>() }));
    const validatedAttributes = resetNodes.map((attribute) => ({
        ...attribute,
        options:
            attribute.type === ATTRIBUTE_NODE_TYPE
                ? validateComposerOptions(attribute, attribute.options, resetNodes, context)
                : attribute.options.map((option) => ({ ...option, brokenValues: new Map<string, string>(), actions: validateComposerActions(option.actions) }))
    }));
    const attributes = validatedAttributes.filter((attribute) => attribute.type === ATTRIBUTE_NODE_TYPE);
    const groups = new Map(validatedAttributes.filter((node) => node.type === ATTRIBUTE_GROUP_NODE_TYPE).map((node) => [node.id, node]));
    const steps = new Map(validatedAttributes.filter((node) => node.type === ATTRIBUTE_STEP_NODE_TYPE).map((node) => [node.id, node]));
    const nameCounts = countValues(attributes, (attribute) => attribute.name);
    const localAttributes = attributes.filter((attribute) => !isConnectedToStore(attribute));
    const localCodeCounts = countValues(localAttributes, (attribute) => attribute.code);
    const storeAttributeCounts = countValues(attributes.filter(isConnectedToStore), (attribute) => attribute.platformAttribute?.id || '');
    const canValidateStoreCatalog = Array.isArray(context.merchantProductOptions) && !context.merchantProductOptionsError;

    validatedAttributes.forEach((node) => {
        if (node.type === ('PreSetContainer' as ComposerAttributeType)) {
            const sourceCount = [node.presetSelection?.compositions, node.presetSelection?.categories, node.presetSelection?.macrocategories].filter(
                (sources) => sources?.length
            ).length;
            if (sourceCount === 0)
                addValidationError(
                    node,
                    ComposerValidationErrorCode.PRESET_SOURCE_REQUIRED,
                    'Choose a grouping mode and at least one pre-set configuration source.'
                );
            if (sourceCount > 1)
                addValidationError(
                    node,
                    ComposerValidationErrorCode.PRESET_SOURCE_MIXED,
                    'Use only one pre-set configuration source type. Reset this container or create separate containers.'
                );
            if (!normalizeComparableValue(node.name))
                addValidationError(node, ComposerValidationErrorCode.PRESET_NAME_REQUIRED, 'This pre-set container needs a name.');
            if (
                (node.parentType === ATTRIBUTE_GROUP_NODE_TYPE && (!node.parentID || !groups.has(node.parentID))) ||
                (node.parentType === ATTRIBUTE_STEP_NODE_TYPE && (!node.parentID || !steps.has(node.parentID)))
            ) {
                addValidationError(
                    node,
                    ComposerValidationErrorCode.PRESET_PARENT_NOT_FOUND,
                    'This pre-set container belongs to a group or step that no longer exists.'
                );
            }
            return;
        }
        if (node.type === ATTRIBUTE_GROUP_NODE_TYPE) {
            if (!normalizeComparableValue(node.name)) {
                addValidationError(node, ComposerValidationErrorCode.GROUP_NAME_REQUIRED, 'This group needs a name.');
            }
            return;
        }

        if (node.type === ATTRIBUTE_STEP_NODE_TYPE) {
            if (!normalizeComparableValue(node.name)) {
                addValidationError(node, ComposerValidationErrorCode.STEP_NAME_REQUIRED, 'This step needs a name.');
            }
            if (node.parentType === ATTRIBUTE_GROUP_NODE_TYPE && (!node.parentID || !groups.has(node.parentID))) {
                addValidationError(node, ComposerValidationErrorCode.STEP_PARENT_NOT_FOUND, 'This step belongs to a group that no longer exists.');
            }
        }
    });

    attributes.forEach((attribute) => {
        const attributeName = formatNodeName(attribute.name, 'This attribute');
        if (!normalizeComparableValue(attribute.name)) {
            addValidationError(attribute, ComposerValidationErrorCode.ATTRIBUTE_NAME_REQUIRED, 'This attribute needs a name.');
        }

        if ((nameCounts.get(normalizeComparableValue(attribute.name)) ?? 0) > 1) {
            addValidationError(
                attribute,
                ComposerValidationErrorCode.ATTRIBUTE_NAME_NOT_UNIQUE,
                `Another attribute uses the name “${attribute.name.trim()}”. Attribute names must be unique.`
            );
        }

        if (!isConnectedToStore(attribute) && !normalizeComparableValue(attribute.code)) {
            addValidationError(attribute, ComposerValidationErrorCode.ATTRIBUTE_CODE_REQUIRED, `The local attribute “${attributeName}” needs a code.`);
        }

        if (!isConnectedToStore(attribute) && (localCodeCounts.get(normalizeComparableValue(attribute.code)) ?? 0) > 1) {
            addValidationError(
                attribute,
                ComposerValidationErrorCode.ATTRIBUTE_CODE_NOT_UNIQUE,
                `Another local attribute uses the code “${attribute.code.trim()}”. Codes must be unique for attributes not connected to the store.`
            );
        }

        if (isConnectedToStore(attribute) && (storeAttributeCounts.get(normalizeComparableValue(attribute.platformAttribute?.id)) ?? 0) > 1) {
            addValidationError(
                attribute,
                ComposerValidationErrorCode.STORE_ATTRIBUTE_NOT_UNIQUE,
                `More than one attribute is connected to the store attribute “${attribute.platformAttribute?.label || attribute.platformAttribute?.id}”.`
            );
        }

        if (attribute.attributeType !== CUSTOMIZABLE_AREA_ATTRIBUTE_TYPE) {
            if (attribute.options.length === 0) {
                addValidationError(
                    attribute,
                    ComposerValidationErrorCode.ATTRIBUTE_WITHOUT_OPTIONS,
                    `The attribute “${attributeName}” must have at least one option.`
                );
            } else {
                const defaultOptions = attribute.options.filter((option) => option.isDefault);
                if (defaultOptions.length === 0) {
                    addValidationError(
                        attribute,
                        ComposerValidationErrorCode.ATTRIBUTE_WITHOUT_DEFAULT_OPTION,
                        `The attribute “${attributeName}” needs one default option.`
                    );
                } else if (defaultOptions.length > 1) {
                    addValidationError(
                        attribute,
                        ComposerValidationErrorCode.ATTRIBUTE_WITH_MULTIPLE_DEFAULT_OPTIONS,
                        `The attribute “${attributeName}” has more than one default option.`
                    );
                }

                if (attribute.enabled !== false && !attribute.options.some((option) => option.enabled !== false)) {
                    addValidationError(
                        attribute,
                        ComposerValidationErrorCode.ATTRIBUTE_WITHOUT_ENABLED_OPTIONS,
                        `The enabled attribute “${attributeName}” needs at least one enabled option.`
                    );
                }
            }
        }

        if (attribute.parentType === ATTRIBUTE_GROUP_NODE_TYPE && (!attribute.parentID || !groups.has(attribute.parentID))) {
            addValidationError(attribute, ComposerValidationErrorCode.ATTRIBUTE_PARENT_NOT_FOUND, 'This attribute belongs to a group that no longer exists.');
        } else if (attribute.parentType === ATTRIBUTE_STEP_NODE_TYPE && (!attribute.parentID || !steps.has(attribute.parentID))) {
            addValidationError(attribute, ComposerValidationErrorCode.ATTRIBUTE_PARENT_NOT_FOUND, 'This attribute belongs to a step that no longer exists.');
        }

        if (context.ecommercePlatform === BIGCOMMERCE_PLATFORM && !isConnectedToStore(attribute)) {
            addValidationError(
                attribute,
                ComposerValidationErrorCode.BIGCOMMERCE_ATTRIBUTE_NOT_CONNECTED,
                `The BigCommerce attribute “${attributeName}” must be connected to a store attribute or modifier.`
            );
        }

        validateStoreCode(attribute, `The attribute “${attributeName}”`);

        if (
            canValidateStoreCatalog &&
            isConnectedToStore(attribute) &&
            !context.merchantProductOptions?.some((candidate) => candidate.optionID === attribute.platformAttribute?.id)
        ) {
            addValidationError(
                attribute,
                ComposerValidationErrorCode.STORE_ATTRIBUTE_NOT_FOUND,
                `The connected store attribute “${attribute.platformAttribute?.label || attribute.platformAttribute?.id}” is no longer available.`
            );
        }

        (attribute.pricings ?? []).forEach((pricing, index) => {
            if (pricing.selectedOptionGuid && !attribute.options.some((option) => option.id === pricing.selectedOptionGuid)) {
                addValidationError(
                    attribute,
                    `${ComposerValidationErrorCode.PRICING_WITHOUT_OPTION}:${index}`,
                    `Price ${index + 1} refers to an option that no longer exists in “${attributeName}”.`
                );
            }
        });

        validateLinks(attribute, attribute.id, attributes);
    });

    return validatedAttributes;
};

export const getComposerNodeValidationMessages = (node: ValidatableNode) => Array.from(new Set(node.brokenValues.values()));

export const getComposerAreaValidationMessages = (area: ComposerAreaNode) => getComposerNodeValidationMessages(area);

export const getComposerOptionValidationMessages = (option: ComposerAttributeOptionNode) => {
    const messages = getComposerNodeValidationMessages(option);
    option.actions.forEach((action, index) => {
        getComposerNodeValidationMessages(action).forEach((message) => messages.push(`Action ${index + 1}: ${message}`));
    });
    return Array.from(new Set(messages));
};

export const getComposerAttributeValidationMessages = (attribute: ComposerAttributeNode) => {
    const messages = getComposerNodeValidationMessages(attribute);
    attribute.options.forEach((option) => {
        getComposerOptionValidationMessages(option).forEach((message) =>
            messages.push(`Option “${formatNodeName(option.name, 'Unnamed option')}”: ${message}`)
        );
    });
    return Array.from(new Set(messages));
};

const getNodeTypeLabel = (node: ComposerAttributeNode) => {
    if (node.type === ATTRIBUTE_GROUP_NODE_TYPE) return 'Group';
    if (node.type === ATTRIBUTE_STEP_NODE_TYPE) return 'Step';
    return 'Attribute';
};

export const getComposerValidationMessages = (nodes: ComposerAttributeNode[], areas: ComposerAreaNode[] = []) => [
    ...nodes
        .filter((node) => [ATTRIBUTE_NODE_TYPE, ATTRIBUTE_GROUP_NODE_TYPE, ATTRIBUTE_STEP_NODE_TYPE].includes(node.type))
        .flatMap((node) =>
            getComposerAttributeValidationMessages(node).map(
                (message) => `${getNodeTypeLabel(node)} “${formatNodeName(node.name, `Unnamed ${getNodeTypeLabel(node).toLowerCase()}`)}”: ${message}`
            )
        ),
    ...areas.flatMap((area) =>
        getComposerAreaValidationMessages(area).map((message) => `Customizable area “${formatNodeName(area.name, 'Unnamed area')}”: ${message}`)
    )
];

/** Non-printable scene nodes can be associated with an area, but cannot receive a printed texture. */
export const getComposerBlockingValidationMessages = (nodes: ComposerAttributeNode[], areas: ComposerAreaNode[] = []) =>
    getComposerValidationMessages(
        nodes,
        areas.map((area) => {
            const currentIDs = [...new Set((area.objects || '').split(';').filter(Boolean))].sort();
            const originalIDs = [...new Set((area.originalMeshSides ?? []).map((link) => link.get('meshID')).filter(Boolean))].sort();
            const brokenValues = new Map(area.brokenValues);
            brokenValues.delete(ComposerValidationErrorCode.AREA_OBJECTS_NOT_MESH);
            if (area.sideID > 0 && currentIDs.join(';') === originalIDs.join(';')) {
                brokenValues.delete(ComposerValidationErrorCode.AREA_OBJECTS_REQUIRED);
            }
            return { ...area, brokenValues };
        })
    );
